import { format } from 'date-fns';
import { getDateFnsLocale } from 'utils/format';
import { HourSlot, StepSpan, PROD_COLOR, WAIT_COLOR, MACHINE_COLOR } from './plan-overview-dialog.types';

export type DayGroup = { dayLabel: string; timestamp: number; count: number };

export const buildDayGroups = (hourSlots: HourSlot[]): DayGroup[] => {
    const groups: DayGroup[] = [];
    for (const slot of hourSlots) {
        const currentGroup = groups[groups.length - 1];
        if (currentGroup && currentGroup.dayLabel === slot.dayLabel) {
            currentGroup.count++;
        } else {
            groups.push({ dayLabel: slot.dayLabel, timestamp: slot.timestamp, count: 1 });
        }
    }
    return groups;
};

/**
 * Day header in the user's language, e.g. "tors 22 okt". The backend's `dayLabel` is
 * English, so it only serves as the grouping key (DD-3802).
 */
export const formatDayGroupLabel = (group: DayGroup): string =>
    format(new Date(group.timestamp), 'EEE d MMM', { locale: getDateFnsLocale() });

export const getSlotColor = (slotIndex: number, span: StepSpan): string => {
    if (span.machineStartSlotIndex != null) {
        if (slotIndex >= span.machineStartSlotIndex) return MACHINE_COLOR;
        if (slotIndex > span.productionEndSlotIndex) return WAIT_COLOR;
    }
    return PROD_COLOR;
};

export const getSlotState = (
    slotIndex: number,
    span: StepSpan,
    hourSlots: HourSlot[],
): {
    inSpan: boolean;
    skipNonWorking: boolean;
    isStart: boolean;
    isEnd: boolean;
    isSingle: boolean;
} => {
    const inSpan = slotIndex >= span.startSlotIndex && slotIndex <= span.endSlotIndex;
    const isMachinePhase = span.machineStartSlotIndex != null && slotIndex >= span.machineStartSlotIndex;
    const skipNonWorking = inSpan && !hourSlots[slotIndex].isWorkingHour && !isMachinePhase;

    if (!inSpan || skipNonWorking) {
        return { inSpan, skipNonWorking, isStart: false, isEnd: false, isSingle: false };
    }

    const prevIsGap =
        slotIndex === span.startSlotIndex ||
        (slotIndex > 0 && !hourSlots[slotIndex - 1].isWorkingHour && !isMachinePhase);

    const nextSlotIndex = slotIndex + 1;
    const nextIsMachinePhase = span.machineStartSlotIndex != null && nextSlotIndex >= span.machineStartSlotIndex;
    const nextIsGap =
        slotIndex === span.endSlotIndex ||
        (nextSlotIndex < hourSlots.length && !hourSlots[nextSlotIndex].isWorkingHour && !nextIsMachinePhase);

    const isStart = prevIsGap;
    const isEnd = nextIsGap;

    return { inSpan, skipNonWorking, isStart, isEnd, isSingle: isStart && isEnd };
};
